import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type CSSProperties,
    type ReactElement,
    type ReactNode,
    type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { color, elevation, presenceLayerStyle, radius, useOverlayMotion } from "@tokens/index";
import { motionStyle } from "../motionStyle";
import { useViewport } from "../viewport";
import { Dialog } from "./Dialog";
import { focusElement, getFocusableElements } from "./focusable";
import { returnFocus as returnFocusTo } from "./focusReturn";
import {
    computePanelPosition,
    seedPanelPosition,
    transformOriginFor,
    type PanelPosition,
    type PopoverPlacement,
} from "./popoverPosition";
import { useEscapeKey } from "./useEscapeKey";
import { POPOVER_Z_INDEX } from "./zIndex";

/**
 * Popover: an anchored floating panel.
 *
 * Portals to `document.body`, positions from the anchor rectangle, flips
 * vertically when it would overflow, shifts horizontally to keep a viewport
 * gutter, and repositions on scroll, resize, and panel size change. Escape
 * and an outside pointer-down close it. Focus returns to the anchor.
 *
 * The position is measured in a layout effect, so the first painted frame is
 * already in place. When the panel itself is not mounted yet, the layout
 * effect seeds top/left from the anchor so the commit is never `0,0`. The
 * panel is never `visibility: hidden`: a hidden element refuses focus, and
 * the initial-focus effect runs before the positioned re-render. The framer
 * `initial` (opacity 0) covers that pre-paint frame instead.
 *
 * Children are retained for the exit animation: a caller that clears its
 * payload in the same render that sets `open={false}` would otherwise leave
 * an empty box fading out.
 *
 * Owns its `AnimatePresence`. With `sheetOnCompact`, the panel renders as a
 * bottom sheet on the `compact` viewport class.
 */

export type { PopoverPlacement } from "./popoverPosition";
export type PopoverRole = "menu" | "listbox" | "dialog";

export interface PopoverProps {
    open: boolean;
    onClose: () => void;
    anchorRef: RefObject<HTMLElement | null>;
    /** Id of the panel, so a combobox can point `aria-controls` at it. */
    id?: string;
    placement?: PopoverPlacement;
    matchAnchorWidth?: boolean;
    minWidth?: number;
    /** Overrides the computed `min(60dvh, available space)` cap. */
    maxHeight?: number | string;
    role?: PopoverRole;
    labelledBy?: string;
    /** Plain-text label of the panel. Use when no visible element labels it. */
    ariaLabel?: string;
    /** Default `"first"`. */
    initialFocus?: "first" | "none" | RefObject<HTMLElement | null>;
    sheetOnCompact?: boolean;
    /** Default `true`. */
    returnFocus?: boolean;
    children: ReactNode;
    testId?: string;
}

const samePosition = (prev: PanelPosition | null, next: PanelPosition | null): boolean => {
    if (prev === next) return true;
    if (prev === null || next === null) return false;
    return (
        prev.top === next.top &&
        prev.left === next.left &&
        prev.width === next.width &&
        prev.availableHeight === next.availableHeight &&
        prev.side === next.side
    );
};

const panelBaseStyle: CSSProperties = {
    position: "fixed",
    zIndex: POPOVER_Z_INDEX,
    background: color.bg.surface,
    color: color.fg.default,
    border: `1px solid ${elevation[2].border}`,
    borderRadius: radius.lg,
    // Elevation travels with the card: opacity + transform are compositor-only,
    // so the shadow fades and scales as one texture instead of popping in
    // after enter or snapping off before exit.
    boxShadow: elevation[2].shadow,
    // The panel keeps its compositor layer for its whole lifetime; see
    // `presenceLayerStyle`.
    ...presenceLayerStyle,
    overflowY: "auto",
    overscrollBehavior: "contain",
    outline: "none",
};

export function Popover({
    open,
    onClose,
    anchorRef,
    id,
    placement = "bottom-start",
    matchAnchorWidth = false,
    minWidth,
    maxHeight,
    role,
    labelledBy,
    ariaLabel,
    initialFocus = "first",
    sheetOnCompact = false,
    returnFocus = true,
    children,
    testId,
}: PopoverProps): ReactElement | null {
    const { isCompact } = useViewport();
    const sheet = sheetOnCompact && isCompact;
    const floating = open && !sheet;
    const overlayMotion = useOverlayMotion("popover");
    const panelRef = useRef<HTMLDivElement>(null);

    const computePosition = useCallback(
        (panelEl: HTMLElement | null = panelRef.current): PanelPosition | null => {
            const anchor = anchorRef.current;
            if (anchor === null || typeof window === "undefined") return null;
            const anchorRect = anchor.getBoundingClientRect();
            const viewport = { width: window.innerWidth, height: window.innerHeight };
            return panelEl === null
                ? seedPanelPosition(anchorRect, placement, viewport, matchAnchorWidth, minWidth)
                : computePanelPosition(
                      anchorRect,
                      { width: panelEl.offsetWidth, height: panelEl.scrollHeight },
                      placement,
                      viewport,
                      matchAnchorWidth,
                  );
        },
        [anchorRef, matchAnchorWidth, minWidth, placement],
    );

    const [position, setPosition] = useState<PanelPosition | null>(null);
    const lastPositionRef = useRef<PanelPosition | null>(null);

    // When open transitions to true or anchor changes, immediately seed position
    const prevAnchorRef = useRef<HTMLElement | null>(null);
    const prevOpenRef = useRef(open);

    if (open) {
        if (!prevOpenRef.current || anchorRef.current !== prevAnchorRef.current) {
            const seeded = computePosition(null);
            if (seeded !== null) {
                lastPositionRef.current = seeded;
            }
        }
    }
    prevOpenRef.current = open;
    prevAnchorRef.current = anchorRef.current;

    const lastChildrenRef = useRef<ReactNode>(children);
    if (open) {
        lastChildrenRef.current = children;
    }
    const panelChildren = open ? children : lastChildrenRef.current;

    const reposition = useCallback(() => {
        const next = computePosition();
        if (next === null) return;
        lastPositionRef.current = next;
        setPosition((prev) => (samePosition(prev, next) ? prev : next));
    }, [computePosition]);

    // The last position is kept while the panel animates out.
    useLayoutEffect(() => {
        if (!floating) return;
        reposition();
    }, [floating, reposition]);

    useEffect(() => {
        if (!floating) return;
        window.addEventListener("scroll", reposition, true);
        window.addEventListener("resize", reposition);
        const panel = panelRef.current;
        // The anchor is observed too: a token field grows as chips wrap while
        // its list stays open, and the panel must follow it.
        const anchor = anchorRef.current;
        const observer =
            typeof ResizeObserver === "function" && panel !== null
                ? new ResizeObserver(() => reposition())
                : null;
        if (observer !== null && panel !== null) observer.observe(panel);
        if (observer !== null && anchor !== null) observer.observe(anchor);
        return () => {
            window.removeEventListener("scroll", reposition, true);
            window.removeEventListener("resize", reposition);
            observer?.disconnect();
        };
    }, [anchorRef, floating, reposition]);

    useEffect(() => {
        if (!floating) return;
        const onPointerDown = (event: globalThis.PointerEvent | MouseEvent): void => {
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (panelRef.current?.contains(target) === true) return;
            if (anchorRef.current?.contains(target) === true) return;
            onClose();
        };
        document.addEventListener("pointerdown", onPointerDown);
        return () => document.removeEventListener("pointerdown", onPointerDown);
    }, [anchorRef, floating, onClose]);

    useEscapeKey(floating, onClose);

    useEffect(() => {
        if (!floating) return;
        const panel = panelRef.current;
        if (panel === null) return;
        // Captured on open so focus returns to the element that opened the panel.
        const anchor = anchorRef.current;
        if (initialFocus === "first") {
            const [first] = getFocusableElements(panel);
            if (!focusElement(first)) panel.focus({ preventScroll: true });
        } else if (initialFocus !== "none") {
            if (!focusElement(initialFocus.current)) panel.focus({ preventScroll: true });
        }
        return () => {
            if (!returnFocus) return;
            if (anchor !== null && anchor.isConnected) returnFocusTo(anchor);
        };
    }, [anchorRef, floating, initialFocus, returnFocus]);

    if (typeof document === "undefined") return null;

    if (sheet) {
        return (
            <Dialog
                open={open}
                onClose={onClose}
                presentation="sheet"
                hideHeader
                labelledBy={labelledBy}
                testId={testId}
            >
                <div id={id} role={role} aria-labelledby={labelledBy} aria-label={ariaLabel}>
                    {panelChildren}
                </div>
            </Dialog>
        );
    }

    const currentPosition = position ?? lastPositionRef.current ?? computePosition(null);
    if (currentPosition !== null) {
        lastPositionRef.current = currentPosition;
    }

    const resolvedMaxHeight =
        maxHeight ??
        (currentPosition !== null
            ? `min(60dvh, ${String(Math.max(currentPosition.availableHeight, 80))}px)`
            : "60dvh");

    const style: CSSProperties = {
        ...panelBaseStyle,
        top: currentPosition?.top ?? 0,
        left: currentPosition?.left ?? 0,
        width: currentPosition?.width,
        minWidth,
        maxHeight: resolvedMaxHeight,
        transformOrigin: transformOriginFor(placement, currentPosition?.side),
    };

    return createPortal(
        <AnimatePresence>
            {open && (
                <motion.div
                    key="popover"
                    ref={panelRef}
                    id={id}
                    role={role}
                    aria-labelledby={labelledBy}
                    aria-label={ariaLabel}
                    tabIndex={-1}
                    data-testid={testId}
                    data-popover=""
                    style={motionStyle(style)}
                    initial={overlayMotion.initial}
                    animate={overlayMotion.animate}
                    exit={overlayMotion.exit}
                    transition={overlayMotion.transition}
                >
                    {panelChildren}
                </motion.div>
            )}
        </AnimatePresence>,
        document.body,
    );
}
